import {
  tryLoadDependencies,
  WebAwesomeComponent,
} from '@/components/webcomponents/Global/loadDependency'
import { getTemplateNavHeaderUtilityStyles } from './NavHeaderUtility.styles'

const t = document.createElement('template')
t.innerHTML = `
<wa-button size="small" appearance="plain" class="utility-icon-button">
  <!-- Search Icon -->
  <wa-icon
    name="search"
    class="header-icon"
    label="Search"
    aria-label="Search"
    alt="Search"
    aria-hidden="false"
    focusable="true"
  ></wa-icon>
</wa-button>
 
`

class NavHeaderUtilitySearch extends HTMLElement {
  private _iconEl: EventTarget | null = null
  private isSearchBoxVisible = false

  constructor() {
    super()

    tryLoadDependencies(document, [WebAwesomeComponent.Icon])

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.appendChild(getTemplateNavHeaderUtilityStyles().content.cloneNode(true))
    shadow.appendChild(t.content.cloneNode(true))
  }

  connectedCallback() {
    document.AscendAwesome?.LogManager?.logDebug('NavHeaderUtilitySearch connectedCallback', this)
    const icon = this.shadowRoot?.querySelector('wa-icon')
    const button = this.shadowRoot?.querySelector('wa-button')

    // Add click listener to button (not icon) so entire button triggers searchbox
    if (button) {
      button.addEventListener('click', this.onIconClick)
      button.addEventListener('keydown', this.onIconKeydown as EventListener)
    }

    if (icon) {
      this._iconEl = icon
    }

    // Initialize search state
    this.eventStateChange()
  }

  disconnectedCallback() {
    const button = this.shadowRoot?.querySelector('wa-button')
    if (button) {
      button.removeEventListener('click', this.onIconClick)
      button.removeEventListener('keydown', this.onIconKeydown as EventListener)
    }

    if (this._iconEl) {
      this._iconEl = null
    }

    document.removeEventListener('click', this.onDocumentClick, true)
  }

  private eventStateChange() {
    this.updateCSSClasses()
    this.dispatchSearchStateEvent()
  }

  private updateCSSClasses() {
    const icon = this.shadowRoot?.querySelector('wa-icon')

    if (icon != null) {
      if (this.isSearchBoxVisible) {
        icon.classList.add('search-active')
        icon.classList.remove('hidden')
      } else {
        icon.classList.remove('hidden', 'search-active')
      }
    }

    if (this.isSearchBoxVisible) {
      this.setAttribute('search-visible', 'true')
    } else {
      this.removeAttribute('search-visible')
    }

    document.AscendAwesome?.LogManager?.logDebug(
      'Setting search-visible attribute',
      this.isSearchBoxVisible,
    )
  }

  private dispatchSearchStateEvent() {
    const searchStateEvent = new CustomEvent('search-state-changed', {
      bubbles: true,
      composed: true,
      detail: {
        visible: this.isSearchBoxVisible,
        component: this,
      },
    })

    this.dispatchEvent(searchStateEvent)
  }

  private onIconClick = (ev: Event) => {
    ev.preventDefault()

    const searchButtonClickedEvent = new CustomEvent('search-button-clicked', {
      bubbles: true,
      composed: true,
      detail: {
        component: this,
      },
    })
    this.dispatchEvent(searchButtonClickedEvent)

    this.toggleSearchBox()
  }

  private onIconKeydown = (ev: KeyboardEvent) => {
    if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
      ev.preventDefault()
      const isOpening = !this.isSearchBoxVisible
      this.toggleSearchBox()
      if (isOpening) {
        this.focusSearchBox()
      }
    }
  }

  private focusSearchBox(): void {
    const header = this.closest('ts-navheader-level0, ts-navheader-level1, ts-navheader-level2')
    const displayMode = window.matchMedia?.('(max-width: 576px)').matches ? 'mobile' : 'desktop'
    const searchBox = header?.querySelector(
      `ts-navheader-search-container[display-mode="${displayMode}"] ts-searchbox`,
    ) as HTMLElement | null

    searchBox?.focus()
  }

  private onDocumentClick = (event: MouseEvent) => {
    if (!this.isSearchBoxVisible) return

    const path = event.composedPath() || [event.target]

    const htmlElements = path.filter((el): el is HTMLElement => el instanceof HTMLElement)

    const clickedOnButton = htmlElements.some((el) => el.tagName.toLowerCase() === 'wa-button')
    const clickedInSearch = htmlElements.some((el) => el.tagName.toLowerCase() === 'ts-searchbox')

    if (!clickedOnButton && !clickedInSearch) {
      this.isSearchBoxVisible = false
      this.eventStateChange()
      document.removeEventListener('click', this.onDocumentClick, true)
    }
  }

  private toggleSearchBox() {
    this.isSearchBoxVisible = !this.isSearchBoxVisible
    this.eventStateChange()

    if (this.isSearchBoxVisible) {
      document.addEventListener('click', this.onDocumentClick, true)
    } else {
      document.removeEventListener('click', this.onDocumentClick, true)
    }
  }
}

if (!customElements.get('ts-navheader-utility-search')) {
  customElements.define('ts-navheader-utility-search', NavHeaderUtilitySearch)
}

export { NavHeaderUtilitySearch }
